import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { resolveImage } from "@/lib/api";
import { COMPANY } from "@/i18n/translations";
import { useLang } from "@/context/LanguageContext";
import { calcMonthlyRate, fmtEUR } from "@/lib/finance";
import { Gauge, Calendar, Zap, Fuel, Heart, MessageCircle, Calculator } from "lucide-react";

const fmtNum = (n) => new Intl.NumberFormat("de-DE").format(n);

export default function VehicleCard({ vehicle, featured = false }) {
  const { t } = useLang();
  const img = vehicle.images?.[0]
    ? resolveImage(vehicle.images[0])
    : "/vehicle-placeholder.jpg";
  const sold = vehicle.status === "sold";
  const fin = calcMonthlyRate(vehicle.price, {
    down: vehicle.finance_down,
    term: vehicle.finance_term,
    balloon: vehicle.finance_balloon,
    annualRate: vehicle.finance_rate,
  });
  const waText = `Hallo DNA Automotive, ich interessiere mich für den ${vehicle.brand} ${vehicle.model} (${fmtEUR(vehicle.price)}).`;

  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className="group relative flex flex-col bg-obsidian-surface border border-white/10 hover:border-brand-light/40 transition-all duration-500 hover:shadow-[0_20px_50px_-20px_rgba(46,224,107,0.25)]"
      data-testid={`vehicle-card-${vehicle.id}`}
    >
      <Link to={`/fahrzeug/${vehicle.id}`} className="block">
        <div className={`relative overflow-hidden ${featured ? "aspect-[16/10]" : "aspect-[16/11]"}`}>
          <img
            src={img}
            alt={`${vehicle.brand} ${vehicle.model} · ${vehicle.year} · ${fmtNum(vehicle.mileage)} km`}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian/70 via-transparent to-transparent" />
          {sold && (
            <span className="absolute top-4 left-4 bg-red-600 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1.5">
              {t.fuhrpark.sold}
            </span>
          )}
          <span className="absolute top-4 right-4 bg-black/60 backdrop-blur text-brand-bright text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 border border-brand-light/30">
            {vehicle.brand}
          </span>
          <button
            aria-label="Favorit"
            data-testid={`favorite-${vehicle.id}`}
            onClick={(e) => { e.preventDefault(); }}
            className="absolute bottom-4 right-4 w-9 h-9 flex items-center justify-center bg-black/50 backdrop-blur border border-white/10 text-white hover:text-brand-bright hover:border-brand-light transition-colors"
          >
            <Heart className="w-4 h-4" />
          </button>
        </div>
      </Link>

      <div className="p-6 flex flex-col flex-1">
        <Link to={`/fahrzeug/${vehicle.id}`}>
          <h3 className={`font-heading font-extrabold uppercase tracking-tight text-white leading-tight ${featured ? "text-2xl md:text-3xl" : "text-xl"} hover:text-brand-bright transition-colors`}>
            {vehicle.model}
          </h3>
        </Link>

        <div className="flex flex-wrap gap-x-4 gap-y-2 mt-4 text-[11px] text-zinc-400 uppercase tracking-wider">
          <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-brand-bright" />{vehicle.year}</span>
          <span className="flex items-center gap-1.5"><Gauge className="w-3.5 h-3.5 text-brand-bright" />{fmtNum(vehicle.mileage)} km</span>
          <span className="flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-brand-bright" />{vehicle.power_hp} PS</span>
          {vehicle.fuel && <span className="flex items-center gap-1.5"><Fuel className="w-3.5 h-3.5 text-brand-bright" />{vehicle.fuel}</span>}
        </div>

        <div className="mt-6 pt-5 border-t border-white/10">
          <div className="flex items-end justify-between">
            <span className="font-heading text-2xl md:text-3xl font-extrabold text-brand-bright leading-none">{fmtEUR(vehicle.price)}</span>
            {vehicle.vat_deductible && (
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">MwSt. ausw.</span>
            )}
          </div>
          {!sold && (
            <div className="mt-2 text-sm text-zinc-300">
              Finanzierung ab <span className="text-white font-bold">{fmtEUR(fin.rate)} / Monat</span>
              <span className="text-zinc-500">*</span>
            </div>
          )}
          {!sold && (
            <div className="text-[10px] text-zinc-500 mt-0.5">
              {fin.annualRate.toFixed(2).replace(".", ",")} % Finanzierung über {fin.partner}
            </div>
          )}
        </div>

        <div className="mt-5 grid grid-cols-3 gap-2">
          <Link
            to={`/fahrzeug/${vehicle.id}`}
            data-testid={`vehicle-details-${vehicle.id}`}
            className="col-span-1 inline-flex items-center justify-center bg-brand text-black text-[11px] font-bold uppercase tracking-wider px-2 py-2.5 hover:bg-brand-hover transition-colors"
          >
            Details
          </Link>
          <Link
            to={`/fahrzeug/${vehicle.id}#finanzierung`}
            data-testid={`vehicle-finance-${vehicle.id}`}
            className="col-span-1 inline-flex items-center justify-center gap-1.5 border border-white/20 text-white text-[11px] font-bold uppercase tracking-wider px-2 py-2.5 hover:border-brand-light hover:text-brand-bright transition-colors"
          >
            <Calculator className="w-3.5 h-3.5" />Finanz.
          </Link>
          <a
            href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent(waText)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`vehicle-whatsapp-${vehicle.id}`}
            className="col-span-1 inline-flex items-center justify-center gap-1.5 border border-white/20 text-white text-[11px] font-bold uppercase tracking-wider px-2 py-2.5 hover:border-brand-light hover:text-brand-bright transition-colors"
          >
            <MessageCircle className="w-3.5 h-3.5" />WhatsApp
          </a>
        </div>
      </div>
    </motion.article>
  );
}
